import { SignUpData, Traveller, Trip, PaginationOptions, PaginatedResult } from '@/types';
import * as signUpRepository from '@/repository/signUpRepository';
import { Unsubscribe } from 'firebase/firestore';

class SignUpService {
  // Existing methods - maintain backward compatibility
  listenToPendingSignUps(tripId: string, callback: (signUps: SignUpData[]) => void): Unsubscribe {
    return signUpRepository.listenToPendingSignUps(tripId, callback);
  }

  // New paginated methods
  async getPendingSignUpsPaginated(
    tripId: string,
    paginationOptions?: PaginationOptions
  ): Promise<PaginatedResult<SignUpData>> {
    return signUpRepository.getPendingSignUpsPaginated(tripId, paginationOptions);
  }

  listenToPendingSignUpsPaginated(
    tripId: string,
    callback: (result: PaginatedResult<SignUpData>) => void,
    paginationOptions?: PaginationOptions
  ): Unsubscribe {
    return signUpRepository.listenToPendingSignUpsPaginated(tripId, callback, paginationOptions);
  }

  async getAllSignUpsPaginated(
    tripId: string,
    paginationOptions?: PaginationOptions
  ): Promise<PaginatedResult<SignUpData>> {
    return signUpRepository.getAllSignUpsPaginated(tripId, paginationOptions);
  }

  listenToAllSignUpsPaginated(
    tripId: string,
    callback: (result: PaginatedResult<SignUpData>) => void,
    paginationOptions?: PaginationOptions
  ): Unsubscribe {
    return signUpRepository.listenToAllSignUpsPaginated(tripId, callback, paginationOptions);
  }

  // CRUD operations remain unchanged
  async deleteSignUp(tripId: string, signUpId: string): Promise<void> {
    return signUpRepository.deleteSignUp(tripId, signUpId);
  }

  async submitSignUp(tripId: string, signUpData: SignUpData): Promise<unknown> {
    return signUpRepository.submitSignUp(tripId, signUpData);
  }

  async confirmSignUp(
    trip: Trip,
    signUp: SignUpData,
    apartmentId: string,
    apartmentTypeId: string | null,
    skiPassType: string | undefined,
    transportationType: string,
    isMember: boolean
  ): Promise<Traveller> {
    if (!trip.id || !signUp.id) {
      throw new Error("Trip ID and Sign Up ID are required for confirmation.");
    }

    // Prepare the new traveller object from the sign-up data
    const newTravellerData: Omit<Traveller, 'id'> = {
      name: signUp.primaryParticipant.name,
      email: signUp.email,
      phone: signUp.phone,
      address: signUp.address,
      dateOfBirth: signUp.primaryParticipant.dateOfBirth,
      isSkiClubMember: isMember,
      skiPassType: skiPassType || signUp.primaryParticipant.preferredSkiPass,
      apartmentId: apartmentId,
      apartmentTypeId: apartmentTypeId,
      membershipFee: isMember ? 0 : trip.defaultMembershipFee,
      notes: signUp.additionalNotes,
      status: 'confirmed',
      signUpId: signUp.id,
      // Neue Felder für Gruppen-Unterstützung - keine undefined Werte
      isPrimaryTraveller: signUp.isPrimaryTraveller ?? true,
      groupSignUpId: signUp.groupSignUpId || signUp.id,
      ...(signUp.isPrimaryTraveller === false && signUp.primarySignUpId ? 
          { primaryTravellerId: signUp.primarySignUpId } : {}),
      // Transport nur hinzufügen wenn nicht leer
      ...(transportationType ? { transportationType } : {}),
    };

    // The repository will handle the atomic transaction
    return await signUpRepository.convertSignUpToTraveller(trip.id, signUp, newTravellerData);
  }
}

export const signUpService = new SignUpService();
